import { useInvestmentAccountDetail, useInvestmentInstrumentDetail } from '@egw/api'
import { GenericError, Image, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'
import { extractHoldingDetail } from '../../utils'

export const AccountHoldingsDetail: FC = () => {
  const { t } = useTranslation('features-holdings')

  const {
    alias = '',
    assetAllocation = '',
    category = '',
    symbol = '',
  } = useParams<{
    alias: string
    assetAllocation: string
    category: string
    symbol: string
  }>()

  const {
    data,
    isLoading: holdingLoading,
    isError: holdingDetailError,
  } = useInvestmentAccountDetail(parseInt(alias), {
    enabled: Boolean(alias),
  })

  const holdingDetail = extractHoldingDetail({
    data,
    assetAllocationParam: assetAllocation,
    categoryParam: category,
    symbolParam: symbol,
  })

  const {
    data: investmetInstrument,
    isLoading: instrumentLoading,
    isError: investmetInstrumentError,
  } = useInvestmentInstrumentDetail(symbol)

  const holdingAttributes =
    holdingDetail && investmetInstrument
      ? [
          {
            label: t('holding_details.labels.holding_name'),
            value: investmetInstrument.name,
          },
          {
            label: t('holding_details.labels.previous_close'),
            value: investmetInstrument.priorClosePrice?.amount
              ? buildBasicCurrency({
                  amount: investmetInstrument.priorClosePrice.amount,
                  currencyCode: holdingDetail.costBasis?.currencyCode,
                })
              : t('holding_details.empty_value'),
          },
          {
            label: t('holding_details.labels.quantity_owned'),
            value: holdingDetail.quantity
              ? t('holding_details.shares', { quantity: Number(holdingDetail.quantity).toFixed(2) })
              : t('holding_details.empty_value'),
          },
          {
            label: t(`holding_details.labels.amount_owned`),
            value: buildBasicCurrency({
              amount: holdingDetail.amount.amount,
              currencyCode: holdingDetail.amount.currencyCode,
            }),
          },
          {
            label: t('holding_details.labels.cost_basis'),
            value: buildBasicCurrency({
              amount: holdingDetail.costBasis?.amount ?? 0,
              currencyCode: holdingDetail.costBasis?.currencyCode ?? 'USD',
            }),
          },
        ]
      : []

  return (
    <>
      <PageHeader
        title={t('holding_details.title')}
        subtitle={t(`category_titles.${category}`)}
        backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_HOLDINGS_DETAILS.backPath
          ?.replace(':alias', alias.toString())
          .replace(':assetAllocation', assetAllocation)
          .replace(':category', category)}
      />
      <div className="egw-container pt-0">
        {instrumentLoading && holdingLoading ? (
          <div className="w-full">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={1} height="100px" className="mb-10" />
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          </div>
        ) : null}
        {investmetInstrumentError || holdingDetailError ? (
          <div className="default-page-width my-20">
            <GenericError errorMessage={t('holdings.overview_fatal_error')} />
          </div>
        ) : null}
        {holdingDetail && investmetInstrument ? (
          <div className="flex flex-col font-light gap-8">
            <div className="text-meta text-3xl flex flex-row gap-6 items-center">
              {investmetInstrument.image ? (
                <Image src={investmetInstrument.image} alt="holding image" className="w-16 h-16" />
              ) : null}
              {investmetInstrument.symbol}
            </div>
            <div className="flex flex-col">
              {holdingAttributes.map(({ label, value }: { label: string; value: string }) => (
                <div
                  key={crypto.randomUUID()}
                  className="border-light flex flex-row justify-between border-b py-4 last-of-type:border-none"
                >
                  <span className="text-meta text-base">{label}</span>
                  <span className="text-base text-header font-semibold ">{value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
